import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { API, formatApiError } from "@/lib/api";
import { RM, initials } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import { StatusBadge } from "@/components/StatusBadge";
import { EmployeeDialog } from "@/components/EmployeeDialog";
import { EmployeeImportDialog } from "@/components/EmployeeImportDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Plus, MagnifyingGlass, PencilSimple, Trash, DownloadSimple, Eye, UploadSimple,
} from "@phosphor-icons/react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function Employees() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detail, setDetail] = useState(null);
  const [preview, setPreview] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/employees").then(({ data }) => setRows(data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const filtered = useMemo(() => {
    const s = q.toLowerCase();
    return rows.filter(
      (r) =>
        r.full_name.toLowerCase().includes(s) ||
        r.employee_no.toLowerCase().includes(s) ||
        (r.department || "").toLowerCase().includes(s)
    );
  }, [rows, q]);

  const openDetail = async (emp) => {
    setDetail(emp);
    setPreview(null);
    try {
      const { data } = await api.get(`/employees/${emp.id}/preview`);
      setPreview(data);
    } catch (e) { /* ignore */ }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/employees/${toDelete.id}`);
      toast.success("Employee removed");
      setToDelete(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const downloadEA = (emp) => {
    const year = new Date().getFullYear();
    const token = localStorage.getItem("pf_token");
    fetch(`${API}/employees/${emp.id}/ea-form/${year}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.blob())
      .then((b) => {
        const url = URL.createObjectURL(b);
        const a = document.createElement("a");
        a.href = url;
        a.download = `EA_${emp.employee_no}_${year}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => toast.error("Could not download EA form"));
  };

  return (
    <Layout
      title="Employees"
      subtitle={`${rows.length} employees on record`}
      actions={
        isAdmin ? (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              data-testid="import-employees-btn"
              onClick={() => setImportOpen(true)}
              className="border-[#E2E5EA] bg-transparent text-[#0F172A] transition-colors duration-200 hover:bg-[#EEF1F5] hover:text-red-500"
            >
              <UploadSimple size={16} className="mr-1.5" /> Import
            </Button>
            <Button
              data-testid="add-employee-btn"
              onClick={() => { setEditing(null); setDialogOpen(true); }}
              className="bg-red-500 font-semibold text-white transition-colors duration-200 hover:bg-red-600"
            >
              <Plus size={16} weight="bold" className="mr-1.5" /> Add Employee
            </Button>
          </div>
        ) : null
      }
    >
      <div className="mb-4 flex items-center gap-3">
        <div className="relative w-full max-w-sm">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" />
          <Input
            data-testid="employee-search"
            placeholder="Search name, number or department…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="border-[#E2E5EA] bg-[#FFFFFF] pl-9 text-[#0F172A] focus-visible:ring-red-500"
          />
        </div>
      </div>

      <div className="overflow-x-auto border border-[#E2E5EA] bg-[#FFFFFF]">
        <table className="w-full text-sm" data-testid="employees-table">
          <thead>
            <tr className="border-b border-[#E2E5EA] text-left text-xs uppercase tracking-wider text-[#6B7280]">
              <th className="px-4 py-3 font-medium">Employee</th>
              <th className="px-4 py-3 font-medium">Department</th>
              <th className="px-4 py-3 font-medium">IC / MyKad</th>
              <th className="px-4 py-3 text-right font-medium">Basic Salary</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 text-right font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-[#6B7280]">Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-[#6B7280]">No employees found.</td></tr>
            ) : (
              filtered.map((r) => (
                <tr key={r.id} data-testid={`employee-row-${r.employee_no}`} className="border-b border-[#EDEFF2] transition-colors duration-150 hover:bg-[#EEF1F5]">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#EEF1F5] text-xs font-semibold text-red-500">
                        {initials(r.full_name)}
                      </div>
                      <div>
                        <p className="font-medium text-[#0F172A]">{r.full_name}</p>
                        <p className="text-xs text-[#6B7280]">{r.employee_no} · {r.position || "—"}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-[#9CA3AF]">{r.department || "—"}</td>
                  <td className="px-4 py-3 tabular-nums text-[#9CA3AF]">{r.ic_number || "—"}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-[#0F172A]">{RM(r.basic_salary)}</td>
                  <td className="px-4 py-3"><StatusBadge status={r.employment_status} /></td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button data-testid={`view-${r.employee_no}`} onClick={() => openDetail(r)} title="View" className="rounded p-1.5 text-[#9CA3AF] hover:bg-[#E2E5EA] hover:text-red-500"><Eye size={16} /></button>
                      {isAdmin && <button data-testid={`edit-${r.employee_no}`} onClick={() => { setEditing(r); setDialogOpen(true); }} title="Edit" className="rounded p-1.5 text-[#9CA3AF] hover:bg-[#E2E5EA] hover:text-blue-400"><PencilSimple size={16} /></button>}
                      <button onClick={() => downloadEA(r)} title="EA Form" className="rounded p-1.5 text-[#9CA3AF] hover:bg-[#E2E5EA] hover:text-emerald-400"><DownloadSimple size={16} /></button>
                      {isAdmin && <button data-testid={`delete-${r.employee_no}`} onClick={() => setToDelete(r)} title="Delete" className="rounded p-1.5 text-[#9CA3AF] hover:bg-[#E2E5EA] hover:text-red-400"><Trash size={16} /></button>}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <EmployeeDialog open={dialogOpen} onOpenChange={setDialogOpen} employee={editing} onSaved={load} />
      <EmployeeImportDialog open={importOpen} onOpenChange={setImportOpen} onImported={load} />

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto border-[#E2E5EA] bg-[#FFFFFF] text-[#0F172A]">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-xl">{detail.full_name}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
                  {[
                    ["Employee No", detail.employee_no], ["Position", detail.position || "—"],
                    ["Department", detail.department || "—"], ["IC / MyKad", detail.ic_number || "—"],
                    ["Email", detail.email || "—"], ["Phone", detail.phone || "—"],
                    ["EPF No", detail.epf_number || "—"], ["SOCSO No", detail.socso_number || "—"],
                    ["EIS No", detail.eis_number || "—"], ["Tax No", detail.tax_number || "—"],
                    ["Bank", detail.bank_name || "—"], ["Account No", detail.bank_account || "—"],
                  ].map(([k, v]) => (
                    <div key={k}>
                      <p className="text-xs uppercase tracking-wider text-[#6B7280]">{k}</p>
                      <p className="tabular-nums text-[#0F172A]">{v}</p>
                    </div>
                  ))}
                </div>

                {preview && (
                  <div className="border border-[#E2E5EA] bg-[#F4F5F7] p-4">
                    <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-red-500">Monthly Statutory Preview</p>
                    <div className="space-y-1.5 text-sm">
                      {[
                        ["Gross Pay", preview.gross],
                        ["EPF (Employee 11%)", preview.epf_employee],
                        ["SOCSO (Employee)", preview.socso_employee],
                        ["EIS (Employee)", preview.eis_employee],
                        ["PCB (Tax)", preview.pcb],
                      ].map(([k, v]) => (
                        <div key={k} className="flex justify-between">
                          <span className="text-[#9CA3AF]">{k}</span>
                          <span className="tabular-nums text-[#0F172A]">{RM(v)}</span>
                        </div>
                      ))}
                      <div className="mt-2 flex justify-between border-t border-[#E2E5EA] pt-2 font-semibold">
                        <span className="text-red-500">Net Pay</span>
                        <span className="tabular-nums text-red-500">{RM(preview.net_pay)}</span>
                      </div>
                    </div>
                  </div>
                )}

                <Button onClick={() => downloadEA(detail)} variant="outline" className="w-full border-[#E2E5EA] bg-transparent text-[#0F172A] hover:bg-[#EEF1F5] hover:text-emerald-400">
                  <DownloadSimple size={16} className="mr-1.5" /> Download EA Form {new Date().getFullYear()}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="border-[#E2E5EA] bg-[#FFFFFF] text-[#0F172A]">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove employee?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#9CA3AF]">
              This will permanently remove {toDelete?.full_name} from records.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#E2E5EA] bg-transparent text-[#0F172A] hover:bg-[#EEF1F5]">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete" onClick={doDelete} className="bg-red-600 text-white hover:bg-red-700">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
